import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../../api';

// Async thunk to fetch recipes data from API
export const fetchRecipes = createAsyncThunk(
  'recipes/fetchRecipes',
  async ({ page = 1, searchTerm="" }, { rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/recipe-list?page=${page}`, {searchTerm});
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch recipes');
    }
  }
);

// Async thunk to delete a recipe
export const deleteRecipe = createAsyncThunk(
  'recipes/deleteRecipe',
  async (recipeId, { dispatch, rejectWithValue }) => {
    try {
      await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/recipes-delete`, { id: recipeId });
      dispatch(fetchRecipes({ page: 1 }));
      return recipeId;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete recipe');
    }
  }
);

// Async thunk to create a new recipe
export const createRecipe = createAsyncThunk(
  'recipes/createRecipe',
  async (recipeData, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/recipes`, recipeData);
      if (response.data.success) {
        dispatch(fetchRecipes({ page: recipeData.page || 1 }));
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create recipe');
    }
  }
);

// Async thunk to update an existing recipe
export const updateRecipe = createAsyncThunk(
  'recipes/updateRecipe',
  async (recipeData, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/recipes/`+recipeData.id, recipeData);
      if (response.data.success) {
        dispatch(fetchRecipes({ page: recipeData.page || 1 }));
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update recipe');
    }
  }
);

const recipesSlice = createSlice({
  name: 'admin_recipes',
  initialState: {
    recipes: [],
    currentRecipePage: 1,
    totalRecipePages: 1,
    loading: false,
    error: null,
    successMessage: null,
  },
  reducers: {
    setRecipePage: (state, action) => {
      state.currentRecipePage = action.payload;
    },
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRecipes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecipes.fulfilled, (state, action) => {
        state.loading = false;
        state.recipes = action.payload.data.data;
        state.currentRecipePage = action.payload.data.current_page || 1;
        state.totalRecipePages = action.payload.data.last_page || 0;
      })
      .addCase(fetchRecipes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteRecipe.fulfilled, (state, action) => {
        state.recipes = state.recipes.filter(recipe => recipe.id !== action.payload);
        state.successMessage = 'Recipe deleted successfully';
      })
      .addCase(deleteRecipe.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(createRecipe.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.successMessage = 'Recipe created successfully';
        } else {
          state.error = action.payload.message; // Handle error from response
        }
      })
      .addCase(createRecipe.rejected, (state, action) => {
        state.error = action.payload.message; // Handle error on rejection
      })
      .addCase(updateRecipe.fulfilled, (state, action) => {
        if (action.payload.success) {
          const index = state.recipes.findIndex(recipe => recipe.id === action.payload.data.id);
          if (index !== -1) {
            state.recipes[index] = action.payload.data; // Update with new data
          }
          state.successMessage = 'Recipe updated successfully';
        } else {
          state.error = action.payload.message; // Handle error from response
        }
      })
      .addCase(updateRecipe.rejected, (state, action) => {
        state.error = action.payload.message; // Handle error on rejection
      });
  },
});

export const { setRecipePage, clearMessages } = recipesSlice.actions;

export default recipesSlice.reducer;
